import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import WifiRoundedIcon from '@mui/icons-material/WifiRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';

import { gatewaysApi } from '../api/gateways';
import GatewaysTable from '../components/Gateways/GatewaysTable';
import GatewayDialog from '../components/Gateways/GatewayDialog';
import GatewayMapView from '../components/Gateways/GatewayMapView';
import EmptyState from '../components/EmptyState';
import { showSoftSuccess } from '../utils/softSuccess';
import { $canEditComplex, $isSettlementTenant } from '../store/auth';
import { $complexes, $activeComplexId } from '../store/complexes';
import { getObjectKindLabels } from '../utils/complexKind';

const STATUS_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'UP', label: 'UP' },
  { value: 'DEGRADED', label: 'DEGRADED' },
  { value: 'DOWN', label: 'DOWN' },
];

const MODE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'PING', label: 'PING' },
  { value: 'MQTT', label: 'MQTT' },
  { value: 'BOTH', label: 'BOTH' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'BS', label: 'Базовая станция' },
  { value: 'USPD', label: 'УСПД' },
];

const SLA_WINDOWS = [
  { value: 24, label: '24 часа' },
  { value: 168, label: '7 дней' },
  { value: 720, label: '30 дней' },
];

export default function GatewaysPage() {
  const navigate = useNavigate();
  const [canManage, complexes, activeComplexId, isSettlementTenant] = useUnit([
    $canEditComplex,
    $complexes,
    $activeComplexId,
    $isSettlementTenant,
  ]);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [items, setItems] = useState([]);
  const [buildingSummary, setBuildingSummary] = useState([]);
  const [slaMap, setSlaMap] = useState({});
  const [slaWindow, setSlaWindow] = useState(24);
  const [search, setSearch] = useState('');
  const [selectedComplexId, setSelectedComplexId] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    monitoring_mode: '',
    gateway_type: '',
  });
  const [viewMode, setViewMode] = useState('table');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('view');
  const [selected, setSelected] = useState(null);
  const [checkingId, setCheckingId] = useState(null);

  const patchGatewayRow = useCallback((gatewayId, nextGateway) => {
    if (!gatewayId || !nextGateway) return;

    setItems((current) =>
      current.map((item) =>
        String(item.id) === String(gatewayId) ? { ...item, ...nextGateway } : item
      )
    );

    setSelected((current) =>
      current && String(current.id) === String(gatewayId)
        ? { ...current, ...nextGateway }
        : current
    );
  }, []);

  const refreshGatewaySla = useCallback(
    async (gatewayId) => {
      if (!gatewayId) return;

      try {
        const slaData = await gatewaysApi.listSla({
          window_hours: slaWindow,
          gateway_ids: String(gatewayId),
        });

        const nextSla = Array.isArray(slaData) ? slaData[0] : null;

        setSlaMap((current) => {
          const next = { ...current };
          if (nextSla) {
            next[String(gatewayId)] = nextSla;
          } else {
            delete next[String(gatewayId)];
          }
          return next;
        });
      } catch (error) {
        console.error('refresh gateway sla failed', error);
      }
    },
    [slaWindow]
  );

  useEffect(() => {
    if (!selectedComplexId && activeComplexId) {
      setSelectedComplexId(String(activeComplexId));
    }
  }, [activeComplexId, selectedComplexId]);

  const complexNameMap = useMemo(() => {
    const map = {};
    complexes.forEach((complex) => {
      map[String(complex.id)] = complex.name || '—';
      if (Array.isArray(complex.buildings)) {
        complex.buildings.forEach((building) => {
          map[String(building.id)] = map[String(building.id)] || building.name || '—';
        });
      }
    });
    return map;
  }, [complexes]);

  const buildingNameMap = useMemo(() => {
    const map = {};
    complexes.forEach((complex) => {
      if (Array.isArray(complex.buildings)) {
        complex.buildings.forEach((building) => {
          map[String(building.id)] = building.name || '—';
        });
      }
    });
    return map;
  }, [complexes]);

  const loadGateways = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await gatewaysApi.list({
        residential_complex_id: selectedComplexId || undefined,
        status: filters.status || undefined,
        monitoring_mode: filters.monitoring_mode || undefined,
        gateway_type: filters.gateway_type || undefined,
      });
      const rows = Array.isArray(data) ? data : [];
      setItems(rows);
      try {
        const summaryRows = await gatewaysApi.listLoRaWanBuildingSummary({
          residential_complex_id: selectedComplexId || undefined,
        });
        setBuildingSummary(Array.isArray(summaryRows) ? summaryRows : []);
      } catch (summaryError) {
        console.error('load lorawan building summary failed', summaryError);
        setBuildingSummary([]);
      }

      const ids = rows.map((gateway) => gateway.id).filter(Boolean);
      if (!ids.length) {
        setSlaMap({});
        return;
      }

      const slaData = await gatewaysApi.listSla({
        window_hours: slaWindow,
        gateway_ids: ids.join(','),
      });
      const nextSlaMap = {};
      (slaData || []).forEach((row) => {
        nextSlaMap[String(row.gateway_id)] = row;
      });
      setSlaMap(nextSlaMap);
    } catch (error) {
      console.error('load gateways failed', error);
      setLoadError('Не удалось загрузить список шлюзов. Попробуйте обновить данные или изменить фильтры.');
      setItems([]);
      setSlaMap({});
    } finally {
      setLoading(false);
    }
  }, [filters, selectedComplexId, slaWindow]);

  useEffect(() => {
    loadGateways();
  }, [loadGateways]);

  const openDialog = (gateway, mode = 'view') => {
    setSelected(gateway);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const handleSave = async (payload) => {
    try {
      if (dialogMode === 'create') {
        await gatewaysApi.create(payload);
        showSoftSuccess('Шлюз добавлен');
      } else if (selected?.id) {
        await gatewaysApi.update(selected.id, payload);
        showSoftSuccess('Сохранено');
      }
      closeDialog();
      await loadGateways();
      window.dispatchEvent(new Event('gateways-changed'));
    } catch (error) {
      console.error('save gateway failed', error);
    }
  };

  const handleCheckNow = useCallback(
    async (gateway) => {
      if (!gateway?.id || checkingId) {
        return;
      }
      setCheckingId(gateway.id);
      try {
        const updatedGateway = await gatewaysApi.check(gateway.id);
        if (updatedGateway) {
          patchGatewayRow(gateway.id, updatedGateway);
        }
        await refreshGatewaySla(gateway.id);
        window.dispatchEvent(new Event('gateways-changed'));
      } catch (error) {
        console.error('check gateway failed', error);
      } finally {
        setCheckingId(null);
      }
    },
    [checkingId, patchGatewayRow, refreshGatewaySla]
  );

  const handleLinkLoRaWanGateway = useCallback(
    async (gateway) => {
      if (!gateway?.id || !gateway.lorawan_suggested_gateway_id) {
        return;
      }
      try {
        await gatewaysApi.update(gateway.id, {
          lorawan_gateway_id: gateway.lorawan_suggested_gateway_id,
          lorawan_gateway_name: gateway.lorawan_suggested_gateway_name || gateway.lorawan_gateway_name || '',
        });
        await loadGateways();
        window.dispatchEvent(new Event('gateways-changed'));
      } catch (error) {
        console.error('link lorawan gateway failed', error);
      }
    },
    [loadGateways]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter((gateway) => {
      const complexName = complexNameMap[gateway.residential_complex_id] || '';
      const buildingName = buildingNameMap[gateway.building_id] || '';
      const haystack = [
        gateway.name,
        gateway.host,
        gateway.mqtt_topic,
        gateway.gateway_type,
        gateway.monitoring_mode,
        gateway.location,
        gateway.address,
        gateway.mac_address,
        gateway.lorawan_gateway_id,
        gateway.lorawan_gateway_name,
        complexName,
        buildingName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, search, complexNameMap, buildingNameMap]);

  const summary = useMemo(
    () =>
      filteredItems.reduce(
        (acc, row) => {
          acc.total += 1;
          if (row.status === 'UP') acc.up += 1;
          else if (row.status === 'DEGRADED') acc.degraded += 1;
          else if (row.status === 'DOWN') acc.down += 1;
          else acc.unknown += 1;
          return acc;
        },
        { total: 0, up: 0, degraded: 0, down: 0, unknown: 0 }
      ),
    [filteredItems]
  );

  const averageSla = useMemo(() => {
    const values = filteredItems
      .map((row) => Number(slaMap[String(row.id)]?.sla_percent))
      .filter((value) => Number.isFinite(value));
    if (!values.length) {
      return null;
    }
    return (values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1);
  }, [filteredItems, slaMap]);

  const lorawanLinkedCount = useMemo(
    () =>
      filteredItems.filter(
        (item) => item.gateway_type === 'BS' && (item.lorawan_linked || item.lorawan_gateway_id)
      ).length,
    [filteredItems]
  );

  const activeFiltersCount = [
    selectedComplexId,
    filters.status,
    filters.monitoring_mode,
    filters.gateway_type,
    search.trim(),
  ].filter(Boolean).length;

  const mappableCount = filteredItems.filter(
    (item) => item.latitude != null && item.longitude != null
  ).length;

  const resetFilters = () => {
    setSearch('');
    setSelectedComplexId('');
    setFilters({
      status: '',
      monitoring_mode: '',
      gateway_type: '',
    });
  };

  const hasRows = filteredItems.length > 0;

  return (
    <div className="home-container" style={{ marginLeft: 24, marginRight: 24, paddingBottom: 24 }}>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: { xs: 2, md: 2.5 },
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: 'var(--surface-bg)',
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(52, 104, 255, 0.12)',
                  color: '#3468FF',
                }}
              >
                <HubRoundedIcon fontSize="small" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Шлюзы и мониторинг
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary">
              Управление УСПД и базовыми станциями, их доступностью, SLA и координатами на карте.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button className="rc-btn" onClick={() => navigate(-1)}>
              ← Назад
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={loadGateways}
              disabled={loading}
            >
              Обновить
            </Button>
            {canManage && (
              <Button variant="contained" onClick={() => openDialog({}, 'create')}>
                Добавить шлюз
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        {[
          {
            key: 'total',
            label: 'Всего шлюзов',
            value: summary.total,
            icon: <HubRoundedIcon fontSize="small" />,
            tone: 'rgba(52, 104, 255, 0.12)',
            color: '#1D4ED8',
          },
          {
            key: 'up',
            label: 'Доступны',
            value: summary.up,
            icon: <WifiRoundedIcon fontSize="small" />,
            tone: 'rgba(34, 197, 94, 0.14)',
            color: '#15803D',
          },
          {
            key: 'attention',
            label: 'Требуют внимания',
            value: summary.degraded + summary.down,
            icon: <WarningAmberRoundedIcon fontSize="small" />,
            tone: 'rgba(245, 158, 11, 0.14)',
            color: '#B45309',
          },
          {
            key: 'map',
            label: 'На карте',
            value: mappableCount,
            icon: <MapRoundedIcon fontSize="small" />,
            tone: 'rgba(14, 165, 164, 0.14)',
            color: '#0F766E',
          },
          {
            key: 'lorawan',
            label: 'Связано LoRaWAN',
            value: lorawanLinkedCount,
            icon: <WifiRoundedIcon fontSize="small" />,
            tone: 'rgba(14, 165, 233, 0.14)',
            color: '#0369A1',
          },
          {
            key: 'sla',
            label: `Средний SLA (${slaWindow}ч)`,
            value: averageSla == null ? '—' : `${averageSla}%`,
            icon: <FilterListRoundedIcon fontSize="small" />,
            tone: 'rgba(99, 102, 241, 0.14)',
            color: '#4338CA',
          },
        ].map((card) => (
          <Paper
            key={card.key}
            variant="outlined"
            sx={{
              flex: 1,
              minWidth: 0,
              p: 1.75,
              borderRadius: 3,
              borderColor: 'rgba(15,23,42,0.08)',
              background: 'var(--surface-bg)',
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  background: card.tone,
                  color: card.color,
                }}
              >
                {card.icon}
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {card.value}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>

      {!loading && buildingSummary.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            mb: 2,
            borderRadius: 3,
            borderColor: 'rgba(15,23,42,0.08)',
            background: 'var(--surface-bg)',
          }}
        >
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems="stretch">
            {buildingSummary.slice(0, 4).map((row) => {
              const problemCount = row.devices_warning + row.devices_no_data + row.devices_one_gateway;
              return (
                <Box
                  key={row.building_id}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    p: 1.1,
                    borderRadius: 2.5,
                    border: '1px solid rgba(148,163,184,0.2)',
                    background: problemCount ? 'rgba(255,247,237,0.72)' : 'rgba(240,253,244,0.68)',
                  }}
                >
                  <Stack spacing={0.65}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                      {row.building_name || 'Дом'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap">
                      <Chip
                        size="small"
                        label={`БС: ${row.base_stations_linked}/${row.base_stations_total}`}
                        color={row.base_stations_warning ? 'warning' : 'default'}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        label={`Приборы: ${row.devices_total}`}
                        variant="outlined"
                      />
                      {problemCount ? (
                        <Chip size="small" color="warning" label={`Проверить: ${problemCount}`} />
                      ) : (
                        <Chip size="small" color="success" label="Связь в норме" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      БС/прибор: {row.gateway_count_avg ?? '—'} · RSSI/SNR:{' '}
                      {row.rssi_avg == null ? '—' : Number(row.rssi_avg).toFixed(1)} /{' '}
                      {row.snr_avg == null ? '—' : Number(row.snr_avg).toFixed(1)}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1.25,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          borderRadius: 3,
          background: 'var(--surface-bg)',
          borderColor: 'rgba(15,23,42,0.08)',
          mb: 2,
        }}
      >
        <Chip
          size="small"
          icon={<FilterListRoundedIcon />}
          label={`Фильтры${activeFiltersCount ? `: ${activeFiltersCount}` : ''}`}
          sx={{ background: 'rgba(37,99,235,0.1)', color: '#1e3a8a' }}
        />
        <TextField
          label="Поиск"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          InputLabelProps={{ shrink: true }}
          placeholder="Название, host, MQTT topic"
          sx={{ minWidth: 240, background: '#fff', borderRadius: 1 }}
          InputProps={{
            startAdornment: <SearchRoundedIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
        />
        <TextField
          label={labels.complex}
          value={selectedComplexId}
          onChange={(event) => setSelectedComplexId(event.target.value)}
          size="small"
          select
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180, background: '#fff', borderRadius: 1 }}
        >
          <MenuItem value="">Все</MenuItem>
          {complexes.map((complex) => (
            <MenuItem key={complex.id} value={String(complex.id)}>
              {complex.name || 'Без названия'}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Статус"
          value={filters.status}
          onChange={(event) => setFilters((state) => ({ ...state, status: event.target.value }))}
          size="small"
          select
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140, background: '#fff', borderRadius: 1 }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Режим"
          value={filters.monitoring_mode}
          onChange={(event) => setFilters((state) => ({ ...state, monitoring_mode: event.target.value }))}
          size="small"
          select
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140, background: '#fff', borderRadius: 1 }}
        >
          {MODE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Тип"
          value={filters.gateway_type}
          onChange={(event) => setFilters((state) => ({ ...state, gateway_type: event.target.value }))}
          size="small"
          select
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160, background: '#fff', borderRadius: 1 }}
        >
          {TYPE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Окно SLA"
          value={slaWindow}
          onChange={(event) => setSlaWindow(Number(event.target.value))}
          size="small"
          select
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 140, background: '#fff', borderRadius: 1 }}
        >
          {SLA_WINDOWS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(event, value) => {
            if (value) setViewMode(value);
          }}
        >
          <ToggleButton value="table">
            <TableRowsRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Таблица
          </ToggleButton>
          <ToggleButton value="map">
            <MapRoundedIcon fontSize="small" sx={{ mr: 0.75 }} />
            Карта
          </ToggleButton>
        </ToggleButtonGroup>
        <Button variant="text" onClick={resetFilters} disabled={activeFiltersCount === 0}>
          Сбросить
        </Button>
      </Paper>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {!loading && hasRows && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.25,
            mb: 2,
            borderRadius: 2.5,
            borderColor: 'rgba(15,23,42,0.08)',
            background: 'rgba(255,255,255,0.78)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              Показано <strong>{filteredItems.length}</strong> из <strong>{items.length}</strong> шлюзов
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {summary.down > 0 && <Chip size="small" color="error" label={`DOWN: ${summary.down}`} />}
              {summary.degraded > 0 && <Chip size="small" color="warning" label={`DEGRADED: ${summary.degraded}`} />}
              {summary.up > 0 && <Chip size="small" color="success" label={`UP: ${summary.up}`} />}
            </Stack>
          </Stack>
        </Paper>
      )}

      {!loading && !hasRows ? (
        <Box sx={{ mb: 2 }}>
          <EmptyState
            icon={items.length === 0 ? 'gateway' : 'search'}
            title={items.length === 0 ? 'Шлюзов пока нет' : 'Ничего не найдено'}
            subtitle={items.length === 0 ? 'Добавьте УСПД или БС' : 'Сбросьте фильтры'}
            actionLabel={
              items.length > 0
                ? 'Сбросить фильтры'
                : canManage
                  ? 'Добавить шлюз'
                  : undefined
            }
            onAction={
              items.length > 0
                ? () => resetFilters()
                : canManage
                  ? () => openDialog({}, 'create')
                  : undefined
            }
          />
        </Box>
      ) : viewMode === 'map' ? (
        <Stack spacing={2}>
          <GatewayMapView
            gateways={filteredItems}
            selectedGatewayId={selected?.id}
            onSelectGateway={(gateway) => openDialog(gateway, 'view')}
            height={520}
          />
          <Paper
            variant="outlined"
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              borderColor: 'rgba(15,23,42,0.08)',
              background: 'rgba(255,255,255,0.82)',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              На карте отображаются только шлюзы, у которых заполнены координаты. Сейчас показано {mappableCount} из {filteredItems.length}.
              Координаты можно задать в карточке шлюза вручную или кликом по мини-карте.
            </Typography>
          </Paper>
        </Stack>
      ) : (
        <GatewaysTable
          rows={filteredItems}
          slaMap={slaMap}
          slaWindowHours={slaWindow}
          loading={loading}
          canManage={canManage}
          onOpen={(row) => openDialog(row, 'view')}
          onEdit={(row) => openDialog(row, 'edit')}
          onCheck={handleCheckNow}
          onLinkLoRaWan={handleLinkLoRaWanGateway}
          checkingId={checkingId}
          complexNameMap={complexNameMap}
          buildingNameMap={buildingNameMap}
        />
      )}

      <GatewayDialog
        open={dialogOpen}
        gateway={selected}
        complexes={complexes}
        mode={dialogMode}
        canManage={canManage}
        onClose={closeDialog}
        onEditRequest={() => setDialogMode('edit')}
        onSave={handleSave}
      />
    </div>
  );
}
